import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PostProcessPresetDialog from "components/control/settings/PostProcessPresetDialog";
import PostProcessRuleCard from "components/control/settings/PostProcessRuleCard";
import PostProcessRuleDialog from "components/control/settings/PostProcessRuleDialog";
import {
    applyPostprocessPreset,
    createPostprocessPreset,
    fetchPostprocessCatalog,
    fetchPostprocessPresets,
    fetchPostprocessRules,
    putPostprocessRules,
} from "lib/api/objectRecognition";
import {
    collectKnownLabels,
    colorFromActions,
    defaultPostProcessConfig,
    defaultRuleStep,
    labelFromActions,
    newStepId,
    normalizeHexColor,
    type PostProcessCatalog,
    type PostProcessConfig,
    type PostProcessPreset,
    type PostProcessStep,
} from "lib/consts/postProcessRules";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { useToast } from "providers/ToastProvider";

type PostProcessRulesPanelProps = {
    host: string | null;
    classLabels: string[];
    disabled?: boolean;
};

const PostProcessRulesPanel = ({
    host,
    classLabels,
    disabled = false,
}: PostProcessRulesPanelProps) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    const [config, setConfig] = useState<PostProcessConfig>(defaultPostProcessConfig());
    const [catalog, setCatalog] = useState<PostProcessCatalog | null>(null);
    const [presets, setPresets] = useState<PostProcessPreset[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [presetDialogOpen, setPresetDialogOpen] = useState(false);
    const [editingStep, setEditingStep] = useState<PostProcessStep | null>(null);

    const load = useCallback(async () => {
        if (!host) return;
        setLoading(true);
        try {
            const [rules, cat, presetPayload] = await Promise.all([
                fetchPostprocessRules(host, isSecure, arcId),
                fetchPostprocessCatalog(host, isSecure, arcId),
                fetchPostprocessPresets(host, isSecure, arcId),
            ]);
            setConfig(rules);
            setCatalog(cat);
            setPresets(presetPayload.presets ?? []);
        } catch (e) {
            addToast({
                type: "error",
                message:
                    toUserFacingApiError(e, t("objectRecognition.postprocessLoadFailed")),
            });
        } finally {
            setLoading(false);
        }
    }, [host, isSecure, arcId, addToast, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const save = async (next: PostProcessConfig) => {
        if (!host) return;
        setSaving(true);
        try {
            const saved = await putPostprocessRules(host, next, isSecure, arcId);
            setConfig(saved);
            addToast({ type: "success", message: t("objectRecognition.postprocessSaved") });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    toUserFacingApiError(e, t("objectRecognition.postprocessSaveFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const updateConfig = (patch: Partial<PostProcessConfig>) => {
        setConfig((prev) => ({ ...prev, ...patch }));
    };

    const handleToggleGlobal = async () => {
        const next = { ...config, enabled: !config.enabled };
        setConfig(next);
        await save(next);
    };

    const handleToggleTemporal = () => {
        updateConfig({
            temporal: { ...config.temporal, enabled: !config.temporal.enabled },
        });
    };

    const handleAddRule = () => {
        setEditingStep(defaultRuleStep(classLabels));
        setDialogOpen(true);
    };

    const handleEdit = (step: PostProcessStep) => {
        setEditingStep({ ...step });
        setDialogOpen(true);
    };

    const handleDelete = (step: PostProcessStep) => {
        const next = {
            ...config,
            steps: config.steps.filter((s) => s.id !== step.id),
        };
        setConfig(next);
        void save(next);
    };

    const handleToggleStep = (step: PostProcessStep) => {
        const next = {
            ...config,
            steps: config.steps.map((s) =>
                s.id === step.id ? { ...s, enabled: !s.enabled } : s,
            ),
        };
        setConfig(next);
        void save(next);
    };

    const handleDialogSave = (step: PostProcessStep) => {
        const exists = config.steps.some((s) => s.id === step.id);
        const steps = exists
            ? config.steps.map((s) => (s.id === step.id ? step : s))
            : [...config.steps, { ...step, id: step.id || newStepId() }];
        const label_colors = { ...(config.label_colors ?? {}) };
        const syncBranch = (actions: PostProcessStep["then"]) => {
            const lbl = labelFromActions(actions);
            const col = colorFromActions(actions);
            if (lbl && col && normalizeHexColor(col)) {
                label_colors[lbl] = normalizeHexColor(col)!;
            }
        };
        syncBranch(step.then);
        syncBranch(step.else);
        const next = { ...config, steps, label_colors };
        setConfig(next);
        void save(next);
    };

    const knownLabels = collectKnownLabels(config, classLabels);
    const labelColorEntries = Object.entries(config.label_colors ?? {}).sort(
        ([a], [b]) => a.localeCompare(b),
    );

    const updateLabelColor = (label: string, color: string) => {
        const hex = normalizeHexColor(color);
        const label_colors = { ...(config.label_colors ?? {}) };
        if (!label.trim()) return;
        if (hex) {
            label_colors[label.trim()] = hex;
        } else {
            delete label_colors[label.trim()];
        }
        updateConfig({ label_colors });
    };

    const removeLabelColor = (label: string) => {
        const label_colors = { ...(config.label_colors ?? {}) };
        delete label_colors[label];
        updateConfig({ label_colors });
    };

    const addLabelColorRow = () => {
        const label_colors = { ...(config.label_colors ?? {}) };
        const base = "new_label";
        let key = base;
        let n = 1;
        while (label_colors[key]) {
            key = `${base}_${n}`;
            n += 1;
        }
        label_colors[key] = "#ff4444";
        updateConfig({ label_colors });
    };

    const handleApplyPreset = async (presetId: string) => {
        if (!host) return;
        setSaving(true);
        try {
            const saved = await applyPostprocessPreset(host, presetId, "replace", isSecure, arcId);
            setConfig(saved);
            addToast({ type: "success", message: t("objectRecognition.postprocessPresetApplied") });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    toUserFacingApiError(e, t("objectRecognition.postprocessPresetFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCreatePreset = async (payload: { label: string; description: string }) => {
        if (!host) return;
        setSaving(true);
        try {
            const { presets: nextPresets } = await createPostprocessPreset(
                host,
                payload,
                isSecure,
                arcId,
            );
            setPresets(nextPresets);
            addToast({ type: "success", message: t("objectRecognition.postprocessPresetCreated") });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    toUserFacingApiError(e, t("objectRecognition.postprocessPresetCreateFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const builtinPresets = presets.filter((p) => p.builtin !== false);
    const customPresets = presets.filter((p) => p.builtin === false);

    const busy = loading || saving || disabled;

    return (
        <div className="flex flex-col gap-3 rounded-r10 border border-black80 bg-black90 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-h5 font-semibold text-white">
                    {t("objectRecognition.postprocessTitle")}
                </h3>
                <label className="flex items-center gap-2 text-h5 text-black40">
                    <input
                        type="checkbox"
                        checked={config.enabled}
                        disabled={busy}
                        onChange={() => void handleToggleGlobal()}
                    />
                    {t("objectRecognition.postprocessEnabled")}
                </label>
            </div>

            <p className="text-h5 text-black40">{t("objectRecognition.postprocessHint")}</p>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    disabled={busy}
                    onClick={handleAddRule}
                    className="rounded-r5 border border-black80 bg-black80 px-3 py-2 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                >
                    {t("objectRecognition.postprocessAddRule")}
                </button>
                <button
                    type="button"
                    disabled={busy || config.steps.length === 0}
                    onClick={() => setPresetDialogOpen(true)}
                    className="rounded-r5 border border-black80 bg-black80 px-3 py-2 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                >
                    {t("objectRecognition.postprocessSavePreset")}
                </button>
                <select
                    className="rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white"
                    disabled={busy || presets.length === 0}
                    defaultValue=""
                    onChange={(e) => {
                        const id = e.target.value;
                        if (id) {
                            void handleApplyPreset(id);
                            e.target.value = "";
                        }
                    }}
                >
                    <option value="">{t("objectRecognition.postprocessImportPreset")}</option>
                    {builtinPresets.length > 0 ? (
                        <optgroup label={t("objectRecognition.postprocessPresetBuiltin")}>
                            {builtinPresets.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.label}
                                </option>
                            ))}
                        </optgroup>
                    ) : null}
                    {customPresets.length > 0 ? (
                        <optgroup label={t("objectRecognition.postprocessPresetCustom")}>
                            {customPresets.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.label}
                                </option>
                            ))}
                        </optgroup>
                    ) : null}
                </select>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => void load()}
                    className="rounded-r5 border border-black80 bg-black80 px-3 py-2 text-h5 text-white hover:bg-black70"
                >
                    {t("common:refresh")}
                </button>
            </div>

            <label className="flex items-center gap-2 text-h5 text-black40">
                <input
                    type="checkbox"
                    checked={config.temporal?.enabled ?? false}
                    disabled={busy}
                    onChange={handleToggleTemporal}
                />
                {t("objectRecognition.postprocessTemporal")}
            </label>

            <div className="flex flex-col gap-2 rounded-r5 border border-black70 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-h5 font-medium text-white">
                        {t("objectRecognition.postprocessLabelColors")}
                    </h4>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={addLabelColorRow}
                        className="rounded-r5 border border-black80 bg-black80 px-2 py-1 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                    >
                        {t("objectRecognition.postprocessAddLabelColor")}
                    </button>
                </div>
                <p className="text-h5 text-black40">
                    {t("objectRecognition.postprocessLabelColorsHint")}
                </p>
                {labelColorEntries.length === 0 ? (
                    <p className="text-h5 text-black40">
                        {t("objectRecognition.postprocessLabelColorsEmpty")}
                    </p>
                ) : null}
                <datalist id="postprocess-label-suggestions">
                    {knownLabels.map((l) => (
                        <option key={l} value={l} />
                    ))}
                </datalist>
                {labelColorEntries.map(([label, color]) => (
                    <div key={label} className="flex flex-wrap items-center gap-2">
                        <input
                            list="postprocess-label-suggestions"
                            className="min-w-[10rem] flex-1 rounded-r5 border border-black80 bg-black70 px-2 py-1.5 text-h5 text-white"
                            value={label}
                            disabled={busy}
                            onChange={(e) => {
                                const next = { ...(config.label_colors ?? {}) };
                                const newLabel = e.target.value;
                                delete next[label];
                                if (newLabel.trim()) {
                                    next[newLabel.trim()] = color;
                                }
                                updateConfig({ label_colors: next });
                            }}
                        />
                        <input
                            type="color"
                            className="h-9 w-10 cursor-pointer rounded border border-black80 bg-black70"
                            value={color}
                            disabled={busy}
                            onChange={(e) => updateLabelColor(label, e.target.value)}
                        />
                        <input
                            className="w-24 rounded-r5 border border-black80 bg-black70 px-2 py-1.5 text-h5 text-white"
                            value={color}
                            disabled={busy}
                            onChange={(e) => updateLabelColor(label, e.target.value)}
                        />
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => removeLabelColor(label)}
                            className="rounded-r5 border border-black80 px-2 py-1 text-h5 text-white hover:bg-black80"
                            aria-label={t("objectRecognition.postprocessRemoveLabelColor", {
                                label,
                            })}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>

            <div className="flex flex-col gap-2">
                {config.steps.length === 0 && !loading ? (
                    <p className="text-h5 text-black40">
                        {t("objectRecognition.postprocessEmpty")}
                    </p>
                ) : null}
                {config.steps.map((step, index) => (
                    <PostProcessRuleCard
                        key={step.id}
                        step={step}
                        index={index}
                        isBusy={busy}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleEnabled={handleToggleStep}
                    />
                ))}
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => void save(config)}
                    className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 text-h5 text-white hover:bg-primary1 disabled:opacity-50"
                >
                    {t("common:save")}
                </button>
            </div>

            <PostProcessRuleDialog
                open={dialogOpen}
                step={editingStep}
                catalog={catalog}
                classLabels={classLabels}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingStep(null);
                }}
                onSave={handleDialogSave}
            />
            <PostProcessPresetDialog
                open={presetDialogOpen}
                busy={busy}
                onClose={() => setPresetDialogOpen(false)}
                onSave={(payload) => void handleCreatePreset(payload)}
            />
        </div>
    );
};

export default PostProcessRulesPanel;
